import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Shield, Award, Users, Building2, Zap, Star, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' or 'annual'

  const pricingTiers = [
    {
      name: "Student Free",
      price: { monthly: 0, annual: 0 },
      description: "Perfect for individual learners exploring Shakespeare",
      features: [
        { name: "Access to 3 complete plays", included: true },
        { name: "Basic quiz functionality", included: true },
        { name: "Pronunciation guides", included: true },
        { name: "Glossary integration", included: true },
        { name: "Progress tracking", included: true },
        { name: "XP and badges", included: true },
        { name: "Limited analytics", included: true },
        { name: "Community support", included: true },
        { name: "Advanced analytics", included: false },
        { name: "Priority support", included: false }
      ],
      cta: "Start Learning",
      ctaLink: "/registration",
      recommended: false,
      color: "blue"
    },
    {
      name: "Educator Premium",
      price: { monthly: 29, annual: 290 },
      description: "Comprehensive tools for classroom management",
      features: [
        { name: "Unlimited play access", included: true },
        { name: "Full quiz creation tools", included: true },
        { name: "Advanced analytics dashboard", included: true },
        { name: "Student management (up to 100)", included: true },
        { name: "Custom content upload", included: true },
        { name: "CSV bulk operations", included: true },
        { name: "Glossary management", included: true },
        { name: "Priority email support", included: true },
        { name: "Professional development resources", included: true },
        { name: "White-label branding", included: false }
      ],
      cta: "Start Free Trial",
      ctaLink: "/registration",
      recommended: true,
      color: "purple",
      savings: "Save $58/year"
    },
    {
      name: "Institutional",
      price: { monthly: "Custom", annual: "Custom" },
      description: "Enterprise solution for schools and districts",
      features: [
        { name: "Unlimited users", included: true },
        { name: "Unlimited content access", included: true },
        { name: "Advanced admin controls", included: true },
        { name: "Custom integrations", included: true },
        { name: "SSO authentication", included: true },
        { name: "Dedicated account manager", included: true },
        { name: "Custom training sessions", included: true },
        { name: "24/7 priority support", included: true },
        { name: "White-label branding", included: true },
        { name: "API access", included: true }
      ],
      cta: "Contact Sales",
      ctaLink: "/contact-us",
      recommended: false,
      color: "indigo"
    }
  ];

  const faqs = [
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes! You can change your subscription level at any time. Upgrades take effect immediately, while downgrades will apply at the end of your current billing cycle."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, MasterCard, American Express) and offer institutional invoicing for enterprise customers."
    },
    {
      question: "Is there a educational discount?",
      answer: "Yes! Verified educators and institutions receive special pricing. Contact our sales team for qualification details and discount rates."
    },
    {
      question: "What\'s included in the free trial?",
      answer: "Educator Premium trials include full access to all features for 14 days. No credit card required. Cancel anytime during the trial period."
    },
    {
      question: "How does institutional licensing work?",
      answer: "Institutional plans are customized based on your organization\'s size and needs. Contact us for a personalized quote and implementation timeline."
    },
    {
      question: "What happens if I cancel?",
      answer: "You can cancel anytime. You\'ll retain access until the end of your current billing period. Student data and progress are preserved for 90 days after cancellation."
    }
  ];

  const trustSignals = [
    { icon: Shield, text: "SOC 2 Type II Certified" },
    { icon: Award, text: "COPPA Compliant" },
    { icon: Users, text: "10,000+ Active Users" },
    { icon: Building2, text: "500+ Partner Schools" }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Transparent Pricing for Every Learner
            </h1>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto mb-8">
              Choose the plan that fits your needs. All plans include our core features with no hidden fees.
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center gap-4 mb-12"
          >
            <span className={`text-lg font-semibold ${billingCycle === 'monthly' ? 'text-white' : 'text-purple-300'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-16 h-8 bg-purple-600 rounded-full transition-colors hover:bg-purple-700"
            >
              <motion.div
                className="absolute top-1 left-1 w-6 h-6 bg-white rounded-full"
                animate={{ x: billingCycle === 'annual' ? 32 : 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            </button>
            <span className={`text-lg font-semibold ${billingCycle === 'annual' ? 'text-white' : 'text-purple-300'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="ml-2 px-3 py-1 bg-green-500/20 text-green-300 text-sm font-semibold rounded-full border border-green-500/30">
                Save 17%
              </span>
            )}
          </motion.div>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers?.map((tier, index) => (
              <motion.div
                key={index}
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border-2 ${
                  tier?.recommended
                    ? 'border-purple-400 shadow-2xl shadow-purple-500/20'
                    : 'border-purple-300/20'
                } hover:scale-105 transition-transform`}
              >
                {tier?.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-purple-600 text-white text-sm font-bold rounded-full flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      Recommended
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{tier?.name}</h3>
                  <p className="text-purple-200 text-sm mb-4">{tier?.description}</p>
                  <div className="mb-2">
                    {typeof tier?.price?.[billingCycle] === 'number' ? (
                      <>
                        <span className="text-5xl font-bold text-white">
                          ${tier?.price?.[billingCycle]}
                        </span>
                        <span className="text-purple-200 ml-2">
                          /{billingCycle === 'monthly' ? 'month' : 'year'}
                        </span>
                      </>
                    ) : (
                      <span className="text-5xl font-bold text-white">
                        {tier?.price?.[billingCycle]}
                      </span>
                    )}
                  </div>
                  {tier?.savings && billingCycle === 'annual' && (
                    <p className="text-green-300 text-sm font-semibold">{tier?.savings}</p>
                  )}
                </div>

                <Link
                  to={tier?.ctaLink}
                  className={`block w-full py-3 px-6 text-center font-bold rounded-lg mb-6 transition-colors ${
                    tier?.recommended
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' :'bg-white/10 hover:bg-white/20 text-white border border-purple-300/30'
                  }`}
                >
                  {tier?.cta}
                </Link>

                <div className="space-y-3">
                  {tier?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {feature?.included ? (
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature?.included ? 'text-purple-100' : 'text-gray-500'}`}>
                        {feature?.name}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Trust Signals */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustSignals?.map((signal, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <signal.icon className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                <p className="text-purple-100 font-semibold">{signal?.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Educational Discounts */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 backdrop-blur-lg rounded-2xl p-8 border border-purple-300/20"
          >
            <div className="flex items-start gap-4">
              <Zap className="w-12 h-12 text-yellow-300 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Educational Discounts Available</h3>
                <p className="text-purple-100 mb-4">
                  Verified educators and educational institutions qualify for special pricing up to 30% off standard rates. 
                  Perfect for schools, districts, and educational organizations looking to implement BardPlay at scale.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-purple-200 text-sm">
                    <strong>Requirements:</strong> Valid .edu email address or official school documentation
                  </p>
                  <p className="text-purple-200 text-sm">
                    <strong>Process:</strong> Submit verification through our educator portal for instant approval
                  </p>
                </div>
                <Link
                  to="/contact-us"
                  className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors"
                >
                  Apply for Discount
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-purple-200">Everything you need to know about pricing and plans</p>
          </motion.div>

          <div className="space-y-6">
            {faqs?.map((faq, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-purple-300/20"
              >
                <div className="flex items-start gap-4">
                  <HelpCircle className="w-6 h-6 text-purple-300 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{faq?.question}</h3>
                    <p className="text-purple-100">{faq?.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-purple-200 mb-8">
              Join thousands of students and educators transforming Shakespeare education
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/registration"
                className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact-us"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border border-purple-300/30 transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;